import type { ComponentType } from 'react';
import { useEffect } from 'react';
import {
  Text,
  Box,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useAppSelector, useAppDispatch } from 'hooks';
import { changeDisclosureAndAttestation as changeDisclosureAndAttestationAuto } from 'reducers/autoPersonalFormReducer';
import { changeDisclosureAndAttestation as changeDisclosureAndAttestationHome } from 'reducers/homePersonalFormReducer';
import { changeDisclosureAndAttestation as changeDisclosureAndAttestationSpecialty } from 'reducers/specialtyFormReducer';
import {
  changeDisclosureAndAttestation as changeDisclosureAndAttestationLife,
  addMissingMandatoryField,
  removeMissingMandatoryField,
} from 'reducers/lifeInsuranceFormReducer';
import { changeDisclosureAndAttestation as changeDisclosureAndAttestationCommercial } from 'reducers/commercialFormReducer';

interface DisclosureAndAttestationProps {
  formStateKey: 'autoPersonalForm' | 'homePersonalForm' | 'specialtyForm' | 'lifeInsuranceForm' | 'commercialForm';
  isRequired?: boolean;
  isLocked?: boolean;
  FormInput?: ComponentType<any>;
  section?: number;
}

const DisclosureAndAttestation = ({
  formStateKey,
  isRequired = true,
  isLocked = false,
  FormInput,
  section,
}: DisclosureAndAttestationProps) => {
  const dispatch = useAppDispatch();

  const brokerName = useAppSelector((state) => {
    if (formStateKey === 'autoPersonalForm') {
      return state.autoPersonalForm.data.disclosure_and_attestation?.broker_name || '';
    }
    if (formStateKey === 'homePersonalForm') {
      return state.homePersonalForm.data.disclosure_and_attestation?.broker_name || '';
    }
    if (formStateKey === 'specialtyForm') {
      return state.specialtyForm.data.disclosure_and_attestation?.broker_name || '';
    }
    if (formStateKey === 'lifeInsuranceForm') {
      return state.lifeInsuranceForm.data.disclosure_and_attestation?.broker_name || '';
    }
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.data.disclosure_and_attestation?.broker_name || '';
    }
    return '';
  });

  const validationErrors = useAppSelector((state) => {
    if (formStateKey === 'specialtyForm') {
      return state.specialtyForm.validationErrors;
    }
    if (formStateKey === 'lifeInsuranceForm') {
      return state.lifeInsuranceForm.validationErrors;
    }
    if (formStateKey === 'commercialForm') {
      return state.commercialForm.validationErrors;
    }
    return {};
  });

  const handleBrokerNameChange = (value: string) => {
    const payload = { name: 'broker_name', value };

    if (formStateKey === 'specialtyForm') {
      dispatch(changeDisclosureAndAttestationSpecialty(payload));
    } else if (formStateKey === 'lifeInsuranceForm') {
      dispatch(changeDisclosureAndAttestationLife(payload));
    } else if (formStateKey === 'autoPersonalForm') {
      dispatch(changeDisclosureAndAttestationAuto(payload));
    } else if (formStateKey === 'homePersonalForm') {
      dispatch(changeDisclosureAndAttestationHome(payload));
    } else if (formStateKey === 'commercialForm') {
      dispatch(changeDisclosureAndAttestationCommercial(payload));
    }
  };

  // Track missing mandatory fields for Life form
  useEffect(() => {
    if (formStateKey === 'lifeInsuranceForm' && isRequired) {
      if (!brokerName) {
        dispatch(
          addMissingMandatoryField({
            name: 'sectionDisclosureAndAttestation',
            section: 'Disclosure and Attestation',
          }),
        );
      } else {
        dispatch(removeMissingMandatoryField('sectionDisclosureAndAttestation'));
      }
    }
  }, [brokerName, formStateKey, isRequired, dispatch]);

  return (
    <Card mb="20px" id="sectionDisclosureAndAttestation">
      <Text fontWeight="bold" fontSize="xl" mb="20px">
        Disclosure and Attestation
      </Text>

      <Box>
        {FormInput && section !== undefined ? (
          <FormInput
            section={section}
            label="Broker Name"
            name="broker_name"
            value={brokerName}
            onChange={(e: any) => handleBrokerNameChange(e.target.value)}
            required={isRequired}
            disabled={isLocked}
          />
        ) : (
          <FormControl
            isRequired={isRequired}
            isInvalid={
              !!validationErrors?.disclosure_and_attestation?.broker_name
            }
          >
            <FormLabel ml="0">Broker Name</FormLabel>
            <Input
              variant="main"
              name="broker_name"
              value={brokerName}
              onChange={(e) => handleBrokerNameChange(e.target.value)}
              placeholder="Enter broker name"
              borderColor={!brokerName && isRequired ? 'red' : 'inherit'}
              disabled={isLocked}
            />
            {validationErrors?.disclosure_and_attestation?.broker_name && (
              <FormErrorMessage>
                {validationErrors.disclosure_and_attestation.broker_name}
              </FormErrorMessage>
            )}
          </FormControl>
        )}
      </Box>

      <Box
        mt="20px"
        p="15px"
        bg="gray.50"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
      >
        <Text fontSize="sm" lineHeight="1.5" color="gray.700">
          I <strong>{brokerName || '[Broker Name]'}</strong> as the broker of
          record on this policy attest to the accuracy of the information
          provided and the validity of the remarks enclosed herein. These
          remarks have been generated based on a thorough dialogue with my
          client and will be appended to the client application for signature. I
          understand that these remarks may be relied upon in a claim situation
          and may result in the denial of coverage.
        </Text>
      </Box>
    </Card>
  );
};

export default DisclosureAndAttestation;
